import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { EChartsOption, ECharts } from 'echarts'
import EChart from './EChart'
import { abbreviateNumber } from './theme'

// A block placed at an exact [xStart,xEnd] x [yStart,yEnd] rectangle on the
// timeline. Flat solid fills (no gradients or shadows); the live draft adds a
// centred tag, a floating window label, and edge grips so it reads as the thing
// you compose.
export interface TimelineBar {
  xStart: number
  xEnd: number
  yStart: number
  yEnd: number
  color: string
  border?: string
  borderType?: 'solid' | 'dashed'
  tooltip?: string
  // Centred tag inside the block (e.g. "25 MW"), shown when it fits.
  label?: string
  labelColor?: string
  // Small pill above the block naming its window (e.g. "2028 - 2032").
  topLabel?: string
  // Draw white grips on the top + right edges to signal drag-to-resize.
  editable?: boolean
}

// The shared visual identity for the live draft block you compose by dragging:
// solid Tellus blue when it fits, cerise-red dashed when it does not. Every
// timeline editor (resale, manual reduction, ...) spreads this onto its draft bar
// so the blocks - and the white edge grips that only read on a solid fill - stay
// identical across charts.
export function draftBarStyle(isValid: boolean): Pick<TimelineBar, 'color' | 'border' | 'borderType'> {
  return isValid
    ? { color: '#0067a5' }
    : { color: '#D83527', border: '#812118', borderType: 'dashed' }
}

// An unavailable stretch of the timeline (e.g. a not-resellable first year).
export interface BlockedRegion {
  until: number
  label?: string
}

interface IntervalTimelineChartProps {
  xDomain: [number, number]
  yMax: number
  // The capacity ceiling line + "available capacity" envelope sit at this value.
  // Defaults to yMax (axis top). Pass a value BELOW yMax when the cap is a soft
  // guideline a block may exceed: the axis then grows past the cap to keep an
  // over-cap block fully visible, with the cap drawn as a reference line inside it.
  capValue?: number
  yAxisName?: string
  bars: TimelineBar[]
  blockedRegion?: BlockedRegion
  // When set, draws a soft "available capacity" envelope across the usable years
  // plus a dashed ceiling line labelled with this text (e.g. "50 MW capacity").
  capacityLabel?: string
  height?: number
  // Makes the bar flagged `editable` draggable on the canvas: the body moves the
  // start, the right edge changes the extent, the top edge changes the height.
  // Proposed values are snapped to xSnap / ySnap and clamped to the domain.
  onEditBar?: (geom: { xStart: number; xEnd: number; yStart: number; yEnd: number }) => void
  xSnap?: number
  ySnap?: number
}

const INK_MUTED = '#6f8998'
const GRIDLINE = '#ccd9e3'

// A capacity-utilisation timeline (ECharts). Blocks are drawn as exact rectangles
// via a custom series; the parent owns all the overlap / stacking / validation.
export const IntervalTimelineChart = ({
  xDomain,
  yMax,
  capValue,
  yAxisName,
  bars,
  blockedRegion,
  capacityLabel,
  height = 320,
  onEditBar,
  xSnap = 1,
  ySnap = 1,
}: IntervalTimelineChartProps) => {
  const option = useMemo<EChartsOption>(() => {
    const usableStart = blockedRegion ? blockedRegion.until : xDomain[0]
    // The cap line / envelope sit at capValue (defaults to the axis top).
    const capY = capValue ?? yMax

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const barSeries: any = {
      type: 'custom',
      z: 3,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderItem: (params: any, api: any) => {
        // Clamp every block to the plotting area so it (and its drag grips) can
        // never render outside the chart. Without this a draft stacked above
        // committed capacity (yStart = offset) whose top (offset + volume) exceeds
        // yMax would overflow the capacity line, pushing its top grip off-canvas
        // and leaving the height un-draggable (only the slider could fix it).
        const bottomLeft = api.coord([Math.max(api.value(0), xDomain[0]), Math.max(api.value(2), 0)])
        const topRight = api.coord([Math.min(api.value(1), xDomain[1]), Math.min(api.value(3), yMax)])
        const b = bars[params.dataIndex] || ({} as TimelineBar)
        const x = bottomLeft[0]
        const y = topRight[1]
        const w = topRight[0] - bottomLeft[0]
        const h = bottomLeft[1] - topRight[1]
        const cx = x + w / 2
        // Flat solid fill - the product uses no gradients or drop-shadows.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children: any[] = [{
          type: 'rect',
          shape: { x, y, width: w, height: h, r: 6 },
          style: {
            fill: b.color,
            stroke: b.border || 'transparent',
            lineWidth: b.border ? 2 : 0,
            lineDash: b.borderType === 'dashed' ? [7, 4] : undefined,
          },
        }]

        // Centred tag (e.g. "25 MW"), only when the block is big enough to hold it.
        if (b.label && w > 46 && h > 22) {
          children.push({
            type: 'text', silent: true,
            style: { text: b.label, x: cx, y: y + h / 2, textAlign: 'center', textVerticalAlign: 'middle', fill: b.labelColor || '#fff', fontSize: 13, fontWeight: 700 },
          })
        }
        // Drag grips on the top + right edges.
        if (b.editable && w > 40 && h > 26) {
          children.push({ type: 'rect', silent: true, shape: { x: x + w - 2.5, y: y + h / 2 - 11, width: 5, height: 22, r: 2.5 }, style: { fill: '#fff', opacity: 0.92 } })
          children.push({ type: 'rect', silent: true, shape: { x: cx - 11, y: y - 2.5, width: 22, height: 5, r: 2.5 }, style: { fill: '#fff', opacity: 0.92 } })
        }
        // Floating window label above the block.
        if (b.topLabel && w > 58) {
          const lw = Math.max(78, b.topLabel.length * 7 + 22)
          const ly = Math.max(2, y - 30)
          children.push({ type: 'rect', silent: true, shape: { x: cx - lw / 2, y: ly, width: lw, height: 22, r: 6 }, style: { fill: '#002338', opacity: 0.92 } })
          children.push({ type: 'text', silent: true, style: { text: b.topLabel, x: cx, y: ly + 11, textAlign: 'center', textVerticalAlign: 'middle', fill: '#fff', fontSize: 11.5, fontWeight: 600 } })
        }

        return { type: 'group', children }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: bars.map((b): any => ({ value: [b.xStart, b.xEnd, b.yStart, b.yEnd], tooltip: b.tooltip })),
    }

    // Ceiling line (capacity) + a dashed boundary at the resellable start.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markLineData: any[] = []
    if (capacityLabel) {
      markLineData.push({
        yAxis: capY,
        lineStyle: { type: [5, 4], color: '#a5bcca', width: 1.25 },
        label: { formatter: capacityLabel, position: 'insideStartTop', color: INK_MUTED, fontSize: 11, fontWeight: 600 },
      })
    }
    if (blockedRegion) {
      markLineData.push({
        xAxis: blockedRegion.until,
        lineStyle: { type: [6, 4], color: '#D83527', width: 1.4 },
        label: { formatter: blockedRegion.label ?? '', position: 'insideStartTop', color: '#812118', fontSize: 11, fontWeight: 600 },
      })
    }
    if (markLineData.length) {
      barSeries.markLine = { silent: true, symbol: 'none', data: markLineData }
    }
    if (blockedRegion) {
      barSeries.markArea = {
        silent: true,
        itemStyle: { color: 'rgba(216,53,39,0.05)' },
        data: [[{ xAxis: xDomain[0] }, { xAxis: blockedRegion.until }]],
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const series: any[] = []
    if (capacityLabel) {
      // Flat "available capacity" envelope behind the blocks (no gradient).
      series.push({
        type: 'line', data: [], silent: true, z: 1,
        markArea: {
          silent: true,
          itemStyle: { color: 'rgba(0,132,214,0.06)' },
          data: [[{ xAxis: usableStart, yAxis: 0 }, { xAxis: xDomain[1], yAxis: capY }]],
        },
      })
    }
    series.push(barSeries)

    return {
      grid: { top: 26, left: 8, right: 22, bottom: 26, containLabel: true },
      xAxis: {
        type: 'value',
        min: xDomain[0],
        max: xDomain[1],
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: GRIDLINE, type: 'dashed' } },
        axisLabel: { color: INK_MUTED, formatter: (v: number) => String(Math.round(v)) },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: yMax,
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: 48,
        nameTextStyle: { color: INK_MUTED, fontSize: 12 },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: GRIDLINE, type: 'dashed' } },
        axisLabel: {
          color: INK_MUTED,
          formatter: (v: number) => (Math.abs(v) >= 1000 ? abbreviateNumber(v) : String(Math.round(v * 100) / 100)),
        },
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255,255,255,0.98)',
        borderWidth: 0,
        padding: [8, 12],
        extraCssText: 'box-shadow:0 6px 24px rgba(5,28,44,0.12);border-radius:8px;',
        textStyle: { color: '#002338', fontSize: 12 },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (p: any) => p.data?.tooltip ?? '',
      },
      series,
    }
  }, [xDomain, yMax, capValue, yAxisName, bars, blockedRegion, capacityLabel])

  // ---- drag-to-edit the editable block, via zrender pointer events ----
  const [chart, setChart] = useState<ECharts>()
  const drag = useRef<{ mode: 'move' | 'x' | 'y' | null; grab: number }>({ mode: null, grab: 0 })
  // What the pointer handlers need, kept current so they can attach once (per
  // chart) yet still read the live geometry as the block re-renders mid-drag.
  const live = useRef({
    bars, onEditBar, xSnap, ySnap, xMax: xDomain[1], xDomainMin: xDomain[0],
    xMin: Math.floor(blockedRegion ? blockedRegion.until : xDomain[0]),
    yMax,
  })
  live.current = {
    bars, onEditBar, xSnap, ySnap, xMax: xDomain[1], xDomainMin: xDomain[0],
    xMin: Math.floor(blockedRegion ? blockedRegion.until : xDomain[0]),
    yMax,
  }

  useEffect(() => {
    if (!chart) return undefined
    const zr = chart.getZr()
    const grid = { xAxisIndex: 0, yAxisIndex: 0 }
    const toData = (ox: number, oy: number) => (chart.convertFromPixel(grid, [ox, oy]) as number[]) || []
    const toPixel = (x: number, y: number) => (chart.convertToPixel(grid, [x, y]) as number[]) || []
    const EDGE = 10
    const editable = () => live.current.bars.find((b) => b.editable)
    // Hit-test against the on-screen (clamped) rectangle, not the raw geometry:
    // an over-capacity draft's true top sits above the plot, so its top grip must
    // be caught at the capacity line where the user actually sees it.
    const clampX = (v: number) => Math.max(live.current.xDomainMin, Math.min(v, live.current.xMax))
    const clampY = (v: number) => Math.max(0, Math.min(v, live.current.yMax))
    const rectOf = (b: TimelineBar) => {
      const bl = toPixel(clampX(b.xStart), clampY(b.yStart))
      const tr = toPixel(clampX(b.xEnd), clampY(b.yEnd))
      return { left: Math.min(bl[0], tr[0]), right: Math.max(bl[0], tr[0]), top: Math.min(bl[1], tr[1]), bottom: Math.max(bl[1], tr[1]) }
    }
    const hitMode = (ox: number, oy: number): 'move' | 'x' | 'y' | null => {
      const b = editable()
      if (!b || !live.current.onEditBar) return null
      const r = rectOf(b)
      if (ox < r.left - EDGE || ox > r.right + EDGE || oy < r.top - EDGE || oy > r.bottom + EDGE) return null
      if (Math.abs(ox - r.right) <= EDGE) return 'x'
      if (Math.abs(oy - r.top) <= EDGE) return 'y'
      if (ox >= r.left && ox <= r.right && oy >= r.top && oy <= r.bottom) return 'move'
      return null
    }
    const cursor = (m: string | null) => zr.setCursorStyle(m === 'x' ? 'ew-resize' : m === 'y' ? 'ns-resize' : m === 'move' ? 'grab' : 'default')

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDown = (e: any) => {
      const m = hitMode(e.offsetX, e.offsetY)
      const b = editable()
      if (!m || !b) return
      drag.current = { mode: m, grab: m === 'move' ? toData(e.offsetX, e.offsetY)[0] - b.xStart : 0 }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onMove = (e: any) => {
      if (!drag.current.mode) { cursor(hitMode(e.offsetX, e.offsetY)); return }
      cursor(drag.current.mode === 'move' ? 'grabbing' : drag.current.mode)
      const b = editable()
      const { onEditBar: cb, xSnap: xs, ySnap: ys, xMin, xMax, yMax: ym } = live.current
      if (!b || !cb) return
      const [dx, dy] = toData(e.offsetX, e.offsetY)
      const snap = (v: number, step: number) => Math.round(v / step) * step
      // Last whole year that still fits inside the (fractional) domain, so a moved
      // or right-resized block never spills past the right edge of the plot.
      const xMaxWhole = Math.floor(xMax)
      let { xStart, xEnd, yStart, yEnd } = b
      if (drag.current.mode === 'move') {
        const width = Math.round(b.xEnd - b.xStart)
        const ns = Math.max(xMin, Math.min(Math.round(snap(dx - drag.current.grab, xs)), xMaxWhole - width))
        xStart = ns
        xEnd = ns + width
      } else if (drag.current.mode === 'x') {
        xEnd = Math.max(Math.round(xStart) + xs, Math.min(snap(dx, xs), xMaxWhole))
      } else {
        yEnd = Math.max(yStart + ys, Math.min(snap(dy, ys), ym))
      }
      cb({ xStart, xEnd, yStart, yEnd })
    }
    const onUp = () => { drag.current.mode = null; cursor(null) }

    zr.on('mousedown', onDown)
    zr.on('mousemove', onMove)
    zr.on('mouseup', onUp)
    zr.on('globalout', onUp)
    return () => {
      zr.off('mousedown', onDown)
      zr.off('mousemove', onMove)
      zr.off('mouseup', onUp)
      zr.off('globalout', onUp)
    }
  }, [chart])

  return <EChart option={option} height={height} notMerge decals={false} onInit={setChart} />
}

export default IntervalTimelineChart
